import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Radio, Typography } from "@mui/material";
import { LuArrowLeft } from "react-icons/lu";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { getUserById } from "../../redux/user/userSlice";
import AppLoader from "../../components/AppLoader/AppLoader";
import {
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
  OFF_WHITE,
  PROFILE_FONT,
} from "../../constants/layout";

const ICON_BLUE = INPUT_BORDER_FOCUS;
const CARD_SHADOW = "0 2px 16px rgba(0, 0, 0, 0.06)";

const cardSx = {
  bgcolor: "#ffffff",
  borderRadius: "16px",
  boxShadow: CARD_SHADOW,
  border: "1px solid rgba(0,0,0,0.04)",
};

interface Preference {
  [key: string]: number;
}

interface UserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  userType?: number;
  preferences?: Preference;
}

const PREFERENCE_OPTIONS: { value: string; label: string }[] = [
  { value: "mutualFunds", label: "Mutual Fund" },
  { value: "corporateFD", label: "Corporate FD" },
  { value: "aif", label: "AIF" },
  { value: "pms", label: "PMS" },
  { value: "bonds", label: "Bonds" },
  { value: "equity", label: "Equity" },
  { value: "privateStock", label: "Private Stock" },
];

const radioSx = {
  color: DARK_NAVY_LIGHT,
  "&.Mui-checked": { color: ICON_BLUE },
  p: 0.25,
  flexShrink: 0,
};

const Preferences: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const [selectedOption, setSelectedOption] = useState<string>("");
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noOptionsMessage, setNoOptionsMessage] = useState<string | null>(null);

  const userId = Number(new URLSearchParams(location.search).get("userId"));

  useEffect(() => {
    const fetchUserData = async () => {
      if (!location.search.includes("userId=")) {
        setError("User ID is not available");
        setLoading(false);
        return;
      }

      if (Number.isNaN(userId)) {
        setError("Invalid User ID");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await dispatch(getUserById({ userId }));
        const data = result.payload?.data?.[0];

        if (!data) {
          setError("User not found");
          setLoading(false);
          return;
        }

        setUserData(data);

        if (data.userType !== undefined) {
          sessionStorage.setItem(`holdings-user-type-${userId}`, String(data.userType));
        }

        if (!data.preferences) {
          setNoOptionsMessage("No preferences available.");
        }
      } catch {
        setError("Failed to fetch user data");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [location.search, dispatch, userId]);

  const displayName =
    [userData?.firstName, userData?.lastName].filter(Boolean).join(" ").trim() || "Investor";
  const initial = (displayName[0] || "I").toUpperCase();

  const handleNext = () => {
    if (!selectedOption) {
      toast.error("Please select a holding type before proceeding.");
      return;
    }
    navigate(`/preference/${selectedOption}/${userId}`, {
      state: { userType: userData?.userType },
    });
  };

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100%",
          bgcolor: OFF_WHITE,
          px: { xs: 3, sm: 5, md: 6, lg: 8 },
          py: { xs: 3, md: 4 },
          fontFamily: PROFILE_FONT,
        }}
      >
        <Box sx={{ maxWidth: 720, mx: "auto", ...cardSx, p: 4, textAlign: "center" }}>
          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 18, fontWeight: 700, color: DARK_NAVY, mb: 1 }}>
            {error}
          </Typography>
          <Button
            onClick={() => navigate("/holdings")}
            startIcon={<LuArrowLeft size={16} />}
            sx={{
              mt: 2,
              fontFamily: PROFILE_FONT,
              textTransform: "none",
              fontWeight: 600,
              color: ICON_BLUE,
            }}
          >
            Back to User List
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: OFF_WHITE,
        px: { xs: 3, sm: 5, md: 6, lg: 8 },
        py: { xs: 3, md: 4 },
        fontFamily: PROFILE_FONT,
      }}
    >
      <AppLoader loading={loading} />

      <Box sx={{ maxWidth: 960, mx: "auto" }}>
        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT, mb: 2 }}>
          Holdings{" "}
          <Box component="span" sx={{ mx: 0.75 }}>
            /
          </Box>
          <Box
            component="span"
            sx={{ cursor: "pointer", "&:hover": { color: DARK_NAVY } }}
            onClick={() => navigate("/holdings")}
          >
            User List
          </Box>
          <Box component="span" sx={{ mx: 0.75 }}>
            /
          </Box>
          <Box component="span" sx={{ color: DARK_NAVY, fontWeight: 600 }}>
            Preferences
          </Box>
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: { xs: 24, md: 28 },
                fontWeight: 700,
                color: DARK_NAVY,
                mb: 0.5,
              }}
            >
              Preferences
            </Typography>
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
              Choose a holding type to view this investor&apos;s portfolio.
            </Typography>
          </Box>

          <Button
            onClick={() => navigate("/holdings")}
            startIcon={<LuArrowLeft size={16} />}
            sx={{
              fontFamily: PROFILE_FONT,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              color: DARK_NAVY_LIGHT,
              borderRadius: "10px",
              px: 2,
              py: 1,
              "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
            }}
          >
            Back
          </Button>
        </Box>

        {!loading && userData && (
          <Box
            sx={{
              ...cardSx,
              p: 2.5,
              mb: 2.5,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                bgcolor: ICON_BLUE,
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: PROFILE_FONT,
                fontWeight: 700,
                fontSize: 20,
                flexShrink: 0,
              }}
            >
              {initial}
            </Box>
            <Box>
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 16, fontWeight: 700, color: DARK_NAVY }}>
                {displayName}
              </Typography>
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT, mt: 0.25 }}>
                {userData.email || "—"}
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
          <Typography
            sx={{
              fontFamily: PROFILE_FONT,
              fontSize: 14,
              fontWeight: 600,
              color: DARK_NAVY,
              mb: 2,
            }}
          >
            Select holding type
          </Typography>

          {noOptionsMessage ? (
            <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: "#D32F2F" }}>
              {noOptionsMessage}
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(3, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
                gap: 1.5,
                mb: 2.5,
              }}
            >
              {PREFERENCE_OPTIONS.map((option) => {
                const isSelected = selectedOption === option.value;

                return (
                  <Box
                    key={option.value}
                    component="button"
                    type="button"
                    onClick={() => setSelectedOption(option.value)}
                    sx={{
                      ...cardSx,
                      p: 1.25,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minHeight: 52,
                      textAlign: "left",
                      cursor: "pointer",
                      border: isSelected ? `2px solid ${ICON_BLUE}` : "1px solid rgba(0,0,0,0.06)",
                      bgcolor: isSelected ? "#EEF2F8" : "#ffffff",
                      boxShadow: isSelected ? "0 2px 10px rgba(30, 58, 95, 0.1)" : "none",
                      borderRadius: "12px",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: ICON_BLUE,
                        boxShadow: "0 2px 10px rgba(30, 58, 95, 0.08)",
                      },
                    }}
                  >
                    <Radio checked={isSelected} size="small" sx={radioSx} tabIndex={-1} />
                    <Typography
                      sx={{
                        fontFamily: PROFILE_FONT,
                        fontSize: 13,
                        fontWeight: 600,
                        color: DARK_NAVY,
                        lineHeight: 1.3,
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              onClick={handleNext}
              disabled={!selectedOption || !!noOptionsMessage}
              sx={{
                fontFamily: PROFILE_FONT,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                bgcolor: ICON_BLUE,
                color: "#ffffff",
                borderRadius: "10px",
                px: 3,
                py: 1.25,
                boxShadow: "none",
                "&:hover": { bgcolor: "#16304f", boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "#d0dff0", color: "#ffffff" },
              }}
            >
              Next
            </Button>
            <Button
              onClick={() => navigate("/holdings")}
              sx={{
                fontFamily: PROFILE_FONT,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                color: DARK_NAVY_LIGHT,
                borderRadius: "10px",
                px: 3,
                py: 1.25,
                border: "1px solid #E0E0E0",
                bgcolor: "#ffffff",
                boxShadow: "none",
                "&:hover": { bgcolor: "#FAFAFA", boxShadow: "none" },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Preferences;
